import apiClient, { safeJson } from '@/api/client';
import {
    LessonMoveErrorCode,
    LessonMoveRequest,
    LessonMoveResponse,
    LessonMoveResult,
    SessionReport,
    SessionReportAvailability,
    SessionReportLessonsResponse,
    SessionReportLessonsResult,
    SessionReportResult,
    SessionReportResolutionRequest,
    SessionReportResolutionResult,
} from './types';
import { describeResolutionError, SESSION_REPORT_TOO_MANY_ERROR_CODE, SessionReportTimeoutError } from './errors';

const statusOf = (error: unknown): number | undefined =>
    (error as { status?: number })?.status ?? (error as { response?: { status?: number } })?.response?.status;

/** ky HTTPError(토큰 갱신 뒤 재요청 경로)는 `.body` 가 없고 `.response` 만 있다. 본문을 직접 읽는다. */
const readErrorBody = async (error: any) => {
    if (error?.body) return error.body;
    const response = error?.response;
    if (!response || typeof response.clone !== 'function') return null;
    try {
        return await response.clone().json();
    } catch {
        return null;
    }
};

/** 공용 클라이언트가 토큰 갱신 뒤 `ky(request)` 로 재요청하면 이 옵션(2분 제한·throwHttpErrors)이 빠진다. */
const isRefreshRetryError = (error: any) => error?.name === 'HTTPError' || error?.name === 'TimeoutError';

type ReportParams = {
    centerId: string;
    yearMonth: string;
    ghostDays: number;
    /** 없으면 전 강사 요약만(회원 행 null). 있으면 그 강사 + 회원 행. */
    coachId?: string;
};

const classify = (status: number | undefined, body: any): SessionReportResult | null => {
    if (status === 404) return { status: 'unavailable' };
    if (status === 400 && body?.errorCode === 'SESSION_REPORT_FUTURE_MONTH') return { status: 'futureMonth' };
    if (status === 503 && body?.errorCode === 'SESSION_REPORT_TIMEOUT') throw new SessionReportTimeoutError(body?.message);
    if (status === 503 && body?.errorCode === SESSION_REPORT_TOO_MANY_ERROR_CODE) {
        throw new SessionReportTimeoutError(undefined, SESSION_REPORT_TOO_MANY_ERROR_CODE);
    }
    return null;
};

const requestReport = ({ centerId, yearMonth, ghostDays, coachId }: ReportParams) => {
    const searchParams: Record<string, string | number> = { yearMonth, ghostDays };
    if (coachId) searchParams.coachId = coachId;
    return apiClient.get(`v1/centers/${centerId}/settlement/session-report`, {
        searchParams,
        timeout: 120000,
        throwHttpErrors: false,
        skipErrorAlertFor: [400, 404, 503],
    } as any);
};

/**
 * @method get
 * @brief 월 세션 현황 보고서(센터 전체 PT 수강권·결제행·일정 집계 — 무거운 요청)
 *
 * 계약(BACKEND_HANDOFF_STAGE1.md §0): 강사 미지정 = 총괄용 요약(회원 행 null),
 * 강사 지정 = 그 강사의 회원 행. 화면은 요약 1회 + 강사를 펼칠 때 강사별로 부른다.
 *
 * - 구서버(경로 없음)는 404 → `unavailable`. 공통 오류 알럿을 띄우지 않는다.
 * - 아직 시작하지 않은 달은 400 `SESSION_REPORT_FUTURE_MONTH` → `futureMonth`.
 * - 503 `SESSION_REPORT_TIMEOUT` → `SessionReportTimeoutError` 를 던진다(서버 문구).
 * - 503 `SESSION_REPORT_TOO_MANY_UNASSIGNED` → 같은 오류로 던진다(웹 문구 「기록이 많아 한 번에 불러오지 못했어요…」).
 * - 그 밖의 실패는 throw 해서 화면이 「다시 시도」를 보인다.
 * - 대형 센터는 응답이 1~2MB 라 기본 20초 제한 대신 2분을 준다.
 * - 토큰 갱신 뒤 재요청(ky 기본 옵션 — 10초 제한, 4xx·5xx throw)이 실패하면, 본문으로 위 계약을
 *   먼저 가린 뒤 그래도 모르면 새 토큰으로 이 옵션 그대로 한 번 더 부른다.
 */
export const getSessionReport = async (params: ReportParams): Promise<SessionReportResult> => {
    let res: Response;
    try {
        res = await requestReport(params);
    } catch (error) {
        if (!isRefreshRetryError(error)) {
            const known = classify((error as any)?.status, (error as any)?.body);
            if (known) return known;
            throw error;
        }
        const status = statusOf(error);
        const known = classify(status, await readErrorBody(error));
        if (known) return known;
        // 갱신 경로가 10초 제한에 끊겼거나 알 수 없는 오류 — 새 토큰으로 원래 옵션 그대로 1회.
        res = await requestReport(params);
    }
    const data = (await safeJson(res)) as any;
    const known = classify(res.status, data);
    if (known) return known;
    if (!res.ok) {
        throw Object.assign(new Error(data?.message || '세션 현황을 불러오지 못했어요.'), { status: res.status });
    }
    // 강사 조회는 centerTotals 가 null 이다(계약 §0-2). 요약 조회에서만 필수로 본다.
    if (!data || !Array.isArray(data.coaches) || (!params.coachId && !data.centerTotals)) {
        throw Object.assign(new Error('세션 현황 응답 형식이 올바르지 않아요.'), { status: res.status });
    }
    return { status: 'ok', report: data as SessionReport };
};

/**
 * @method get
 * @brief 세션 현황 탭을 보여 줄지 정하는 가벼운 사전 조회.
 *
 * 보고서 본 요청은 센터 전체를 집계해 무겁다. 그래서 `yearMonth` 없이 부른다 —
 * 새 서버는 인가를 통과한 뒤 Joi 400 을 바로 돌려주고(집계 안 함), 구서버는 경로가 없어 404 다.
 * (400 = 「기능 있음」은 의도다. 서버 라우트가 auth → centerSettlementAccess(403) → validate(400) 순서라
 *  400 은 인가를 통과했다는 뜻이다 — rappo src/routes/v1/settlement.route.ts. 10/19 재확인.)
 * 탭을 실제로 보일지는 이 답 하나로 menuVisibility.ts 가 정한다(10/19부터 정산 방식·정산 분류는 보지 않는다).
 *   - 400 → 이 서버에 기능이 있고 이 계정이 볼 수 있다 → `available`
 *   - 404(구서버)·403(권한 없음) → `unavailable`(확정이라 캐시한다)
 *   - 네트워크 오류·5xx·그 밖 → throw. 오류는 캐시된 답이 아니라 다음 진입 때 다시 묻는다
 *     (그 사이 탭은 숨긴다 — 기존 화면 그대로).
 * 200 이 오는 일은 없지만(필수 쿼리 누락), 오면 노출로 본다.
 */
export const probeSessionReport = async (centerId: string): Promise<SessionReportAvailability> => {
    let status: number | undefined;
    try {
        const res = await apiClient.get(`v1/centers/${centerId}/settlement/session-report`, {
            throwHttpErrors: false,
            skipErrorAlertFor: [400, 403, 404, 500, 502, 503, 504],
        } as any);
        if (res.ok) return 'available';
        status = res.status;
    } catch (error) {
        status = statusOf(error);
        if (status === undefined) throw error;
    }
    if (status === 400) return 'available';
    if (status === 404 || status === 403) return 'unavailable';
    throw Object.assign(new Error('세션 현황 사용 여부를 확인하지 못했어요.'), { status });
};

// ───────────── 수업 옮기기 ─────────────

/**
 * @method get
 * @brief 세션 현황 회원 창의 「수업 보기」 — 한 수강권의 수업과 옮길 수 있는 후보 수강권.
 * 404(구서버 경로 없음·수강권 없음·다른 센터)는 구분하지 않고 `unavailable`. 그 밖 실패는 throw.
 */
export const getSessionReportLessons = async ({
    centerId,
    membershipId,
    from,
    to,
    cursor,
}: {
    centerId: string;
    membershipId: string;
    from?: string;
    to?: string;
    cursor?: string | null;
}): Promise<SessionReportLessonsResult> => {
    const searchParams: Record<string, string> = {};
    if (from) searchParams.from = from;
    if (to) searchParams.to = to;
    if (cursor) searchParams.cursor = cursor;
    let res: Response;
    try {
        res = await apiClient.get(
            `v1/centers/${centerId}/settlement/session-report/memberships/${membershipId}/lessons`,
            { searchParams, throwHttpErrors: false, skipErrorAlertFor: [400, 404] } as any,
        );
    } catch (error) {
        if (statusOf(error) === 404) return { status: 'unavailable' };
        throw error;
    }
    if (res.status === 404) return { status: 'unavailable' };
    const data = (await safeJson(res)) as any;
    if (!res.ok || !data || !Array.isArray(data.lessons) || !Array.isArray(data.targets)) {
        throw Object.assign(new Error(data?.message || '수업 목록을 불러오지 못했어요.'), { status: res.status });
    }
    return { status: 'ok', data: data as SessionReportLessonsResponse };
};

const KNOWN_MOVE_CODES: LessonMoveErrorCode[] = [
    'MOVE_ID_REUSED',
    'SCHEDULE_MEMBERSHIP_CHANGED',
    'LESSON_MOVE_TARGET_FULL',
    'LESSON_MOVE_TARGET_NOT_ELIGIBLE',
    'LESSON_MOVE_TARGET_NEEDS_CONFIRM',
    'LESSON_NOT_MOVABLE',
    'LESSON_MOVE_LIMIT',
    'LESSON_MOVE_BUSY',
];

/** 응답 상태·본문 → 결과. 쓰기 여부를 모르면 반드시 `unknown`(확정 실패로 보이면 사람이 다시 눌러 두 번 옮길 수 있다). */
export const classifyLessonMoveResponse = (status: number | undefined, body: any): LessonMoveResult => {
    const code = body?.errorCode ?? body?.code;
    if (status === undefined) return { status: 'unknown' };
    if (status === 404) return { status: 'rejected', code: 'NOT_FOUND' };
    if (status === 403) return { status: 'rejected', code: code === 'VIEW_ONLY' ? 'VIEW_ONLY' : 'UNKNOWN_ERROR' };
    if (status === 503 && code === 'LESSON_MOVE_BUSY') return { status: 'rejected', code: 'LESSON_MOVE_BUSY' };
    if (status === 409 && KNOWN_MOVE_CODES.includes(code)) return { status: 'rejected', code };
    if (status >= 400 && status < 500) return { status: 'rejected', code: 'UNKNOWN_ERROR' };
    // 503 LESSON_MOVE_RESULT_UNKNOWN, 그 밖 5xx — 서버가 썼는지 모른다
    return { status: 'unknown' };
};

/**
 * @method post
 * @brief 수업 한 건을 다른 수강권으로 옮긴다. 같은 `moveId` 를 다시 보내면 서버는 이미 옮긴 경우 다시 쓰지 않는다.
 * 공용 오류 알럿은 띄우지 않는다(화면이 코드별 문구를 직접 보인다).
 */
export const postSessionReportLessonMove = async (
    centerId: string,
    body: LessonMoveRequest,
): Promise<LessonMoveResult> => {
    let res: Response;
    try {
        res = await apiClient.post(`v1/centers/${centerId}/settlement/session-report/lesson-moves`, {
            json: body,
            throwHttpErrors: false,
            skipErrorAlertFor: [400, 403, 404, 409, 500, 502, 503, 504],
        } as any);
    } catch (error) {
        // 토큰 갱신 재요청 경로(ky HTTPError)는 본문을 직접 읽는다. 상태가 없으면 네트워크 — 결과 모름.
        return classifyLessonMoveResponse(statusOf(error), await readErrorBody(error));
    }
    const data = (await safeJson(res)) as any;
    if (res.ok && data && typeof data.scheduleId === 'string') return { status: 'ok', data: data as LessonMoveResponse };
    if (res.ok) return { status: 'unknown' };
    return classifyLessonMoveResponse(res.status, data);
};

/**
 * @method get
 * @brief 「수업 보기」를 보일지 정하는 가벼운 사전 조회(세션 현황 탭의 probeSessionReport 와 같은 방식).
 * 형식이 틀린 수강권 id 로 부른다 — 새 서버는 인가 뒤 Joi 400(조회 안 함), 구서버는 경로가 없어 404.
 *   400 → available, 404·403 → unavailable, 그 밖 → throw(다음에 다시 묻는다, 그 사이 버튼은 숨긴다).
 */
export const probeSessionReportLessons = async (centerId: string): Promise<SessionReportAvailability> => {
    let status: number | undefined;
    try {
        const res = await apiClient.get(`v1/centers/${centerId}/settlement/session-report/memberships/probe/lessons`, {
            throwHttpErrors: false,
            skipErrorAlertFor: [400, 403, 404, 500, 502, 503, 504],
        } as any);
        status = res.status;
    } catch (error) {
        status = statusOf(error);
        if (status === undefined) throw error;
    }
    if (status === 400) return 'available';
    if (status === 404 || status === 403) return 'unavailable';
    throw Object.assign(new Error('수업 보기 사용 여부를 확인하지 못했어요.'), { status });
};

const resolutionFailure = (status: number | undefined, data: any): SessionReportResolutionResult => ({
    status: 'error', message: describeResolutionError(status, data?.errorCode ?? data?.code),
    ...((data?.errorCode ?? data?.code) === 'SESSION_REPORT_RESOLUTION_REQUEST_ID_REUSED' ? { renewRequest: true } : {}),
});

const requestResolution = async (path: string, body?: SessionReportResolutionRequest): Promise<SessionReportResolutionResult> => {
    try {
        const res = await apiClient.post(path, {
            ...(body ? { json: body } : {}),
            noAuthRetry: true,
            retry: 0,
            throwHttpErrors: false,
            skipErrorAlertFor: [400, 403, 404, 409, 500, 502, 503, 504],
        } as any);
        const data = (await safeJson(res)) as any;
        if (res.ok && typeof data?.resolution?.resolutionId === 'string') {
            if (body && data.resolution.active === false) return { status: 'changed' };
            return { status: 'ok' };
        }
        return resolutionFailure(res.status, data);
    } catch (error) {
        // 토큰 갱신 뒤 HTTPError 도 본문을 읽는다. 응답 유실은 같은 요청 번호로 재시도한다.
        const data = await readErrorBody(error);
        return resolutionFailure(statusOf(error), data);
    }
};

export const postSessionReportResolution = (centerId: string, body: SessionReportResolutionRequest) =>
    requestResolution(`v1/centers/${centerId}/settlement/session-report/resolutions`, body);

export const revokeSessionReportResolution = (centerId: string, resolutionId: string) =>
    requestResolution(`v1/centers/${centerId}/settlement/session-report/resolutions/${resolutionId}/revoke`);
